import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { getTheme, setTheme as applyTheme, type ThemeName } from "@/config/theme";

interface ThemeContextValue {
  theme: ThemeName;
  setTheme: (name: ThemeName) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Wraps the app and provides theme state to all descendants. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(() => getTheme());

  const setTheme = useCallback((name: ThemeName) => {
    applyTheme(name);   // sets data-theme on <html>, persists to localStorage, clears CSS var cache
    setThemeState(name); // triggers React re-render for all context consumers
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Access the current theme, a setter, and a toggle from any component.
 * Must be used inside <ThemeProvider>.
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
